import { type ReactNode, useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuSave, LuTrash2 } from 'react-icons/lu';

import { CreditFormCard } from './CreditFormCard';
import { DeleteCreditModal } from './DeleteCreditModal';
import type { SchemaCreditRequestForm } from '../../../schemas/creditRequestForm.schema';
import { LoanTypeKeys } from '../../../types';
import type { Credit, LoanType } from '../../../types';
import { useExpandedItem } from '../../context/ExpandedItemContext';
import { useExistingLoanActions } from '../../hooks/useExistingLoanActions';
import { getCommandName } from '../../utils/getCommandName';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { type SchemaExistingLoans } from '~/features/leadDetails/schemas/existingLoans.schema';
import { canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';

type LoanCardProps = {
    children: ReactNode;
    name: `${keyof SchemaExistingLoans}.${number}`;
    credit: Credit;
};

const LoanCard: React.FC<LoanCardProps> = ({ children, credit, name }) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { setExpandedItem } = useExpandedItem();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const {
        // eslint-disable-next-line unused-imports/no-unused-vars -- must subscribe to formStates to trigger re-renders
        formState: { dirtyFields, isDirty },
        getFieldState,
        getValues,
        setValue,
    } = useFormContext<SchemaCreditRequestForm>();
    const source = useWatch<SchemaExistingLoans, `${typeof name}.source`>({ name: `${name}.source` });
    const loanType = name.split('.')[0] as keyof SchemaExistingLoans;
    const canChange = canExecuteForAttribution({
        attribution: credit.gehoertZuAntragsteller,
        commandName: getCommandName({ action: 'change', loanType }),
        permissions,
    });
    const canRemove = canExecuteForAttribution({
        attribution: credit.gehoertZuAntragsteller,
        commandName: getCommandName({ action: 'remove', loanType }),
        permissions,
    });

    const {
        changeMutation: { mutate: changeExistingLoan },
        removeMutation: { mutate: removeExistingLoan },
        switchLoanTypeMutation: { mutate: switchLoanTypeMutation },
    } = useExistingLoanActions({
        loanRequestId: leadId,
    });

    const isCurrentCreditInLead = (currentCredit: Credit) => {
        const existingCredits = lead?.haushalt.verbindlichkeiten[loanType] || [];
        const existingCreditsIds = existingCredits?.map((c) => c.id);
        return currentCredit.id && existingCreditsIds.includes(currentCredit.id);
    };

    const fieldIsDirty = getFieldState(`${name}`).isDirty;

    const handleChange = () => {
        const currentCredit = getValues(`${name}`);
        changeExistingLoan({ loanType, payload: currentCredit }, { onSuccess: () => setExpandedItem(null) });
    };

    const handleRemove = () => {
        if (credit.id && isCurrentCreditInLead(credit)) {
            removeExistingLoan({
                loanType,
                payload: { gehoertZuAntragsteller: credit.gehoertZuAntragsteller, id: credit.id },
            });
        }
        setExpandedItem(null);
    };

    const changeLoanType = (newLoanType: keyof SchemaExistingLoans) => {
        if (loanType === newLoanType) return;

        let currentCredit = getValues(`${name}`);

        if ('urspruenglicherKreditbetrag' in currentCredit) {
            currentCredit = {
                ...currentCredit,
                // When the key is not in the credit type, it will be ignored
                beanspruchterBetrag: currentCredit.urspruenglicherKreditbetrag ?? null,
            };
        }

        if ('beanspruchterBetrag' in currentCredit) {
            currentCredit = {
                ...currentCredit,
                // When the key is not in the credit type, it will be ignored
                urspruenglicherKreditbetrag: currentCredit.beanspruchterBetrag ?? null,
            };
        }

        const oldLoans = getValues(`${loanType}`) || [];

        const updatedOldLoans = oldLoans.filter((c) => c.id !== currentCredit.id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(`${loanType}` as LoanType, updatedOldLoans as any);

        const newLoans = getValues(`${newLoanType}`) || [];

        const updatedNewLoans = [...newLoans, currentCredit];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(`${newLoanType}` as LoanType, updatedNewLoans as any);

        switchLoanTypeMutation({
            newLoanType,
            oldLoanType: loanType,
            payload: currentCredit,
        });
    };

    return (
        <>
            <DeleteCreditModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                onConfirm={handleRemove}
            />
            {/* 100cqw = visible width of the horizontally scrolling table (@container in RefinancePlanner) */}
            <div className="sticky left-3 w-[calc(100cqw-1.5rem)]">
                <CreditFormCard
                    title={`${t(`EXISTING_LOANS.TYPES.${loanType}`)} – ${t(
                        `EXISTING_LOANS.SOURCES.${source || 'UNKNOWN'}`,
                    )}`}
                    headerActions={
                        <DropdownMenu<LoanType>
                            aria-label={t('BANK_CREDIT_REQUEST.REFINANCE_PLANNER.SELECT_LOAN_TYPE')}
                            options={LoanTypeKeys.map((value) => ({
                                label: t(`EXISTING_LOANS.TYPES.${value}`),
                                value,
                            }))}
                            value={loanType}
                            onChange={(value) => changeLoanType(value as LoanType)}
                            disabled={!canChange}
                        />
                    }
                    actions={
                        <>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                iconLeft={<LuTrash2 />}
                                onClick={() => setOpenDeleteModal(true)}
                                disabled={!canRemove}
                            >
                                {t('BUTTONS.DELETE')}
                            </Button>
                            <Button iconLeft={<LuSave />} onClick={handleChange} disabled={!fieldIsDirty || !canChange}>
                                {t('BUTTONS.SAVE')}
                            </Button>
                        </>
                    }
                >
                    {children}
                </CreditFormCard>
            </div>
        </>
    );
};

export default LoanCard;
